import { useMutation } from "@apollo/client/react"
import { Dialog, Alert } from "@mui/material"
import { useFormikContext } from "formik"
import React, { useState } from "react"

import { useLocale } from "@/contexts/locale"
import {
  PublicCreateReservationDocument,
  PublicModifyReservationDocument,
  PublicModifyReservationErrorEnum,
  PublicCreateReservationErrorEnum,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { type ReservationFormValues } from "@/pages/public/venue"
import { type BookingPolicyTileViewModel, type BookingPolicySectionResult } from "@/shared/booking_policy"
import { type OpeningHour } from "@/utils/opening_hours"

import BookingPolicyStep from "./booking_policy_step"
import GuestInformationStep from "./guest_information_step"
import ReservationConfirmationStep from "./reservation_confirmation_step"
import ReservationDetailsStep from "./reservation_details_step"

type Table = {
  id: string
  name: string
  minCapacity?: number | null
  maxCapacity?: number | null
  tableGroup: {
    id: string
    name: string
  }
}

type VenuePlan = {
  id: string
  sectionName?: string | null
  plan?: string | null
  position: number
  default: boolean
}

type OriginalReservation = {
  id: string
  startTime: string
  endTime: string
  peopleAmount: number
  tableId: string
}

type ReservationModalProps = {
  open: boolean
  onClose: () => void
  onMobileBack?: () => void
  venueSlug: string
  venueName: string
  venueLogoUrl?: string | null
  venuePlans: VenuePlan[]
  address?: string | null
  city?: string | null
  tables: Table[]
  openingHours: OpeningHour[]
  timezone: string
  country: string
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  holdDurationSeconds?: number | null
  mobileSheet?: boolean
  bookingPolicyTiles?: BookingPolicyTileViewModel[]
  bookingPolicySection?: BookingPolicySectionResult | null
  editMode?: boolean
  originalReservation?: OriginalReservation | null
  reservationToken?: string
  onModifyComplete?: (newReservationId: string) => void
}

type Step = "details" | "booking-policy" | "guest-info" | "confirmation"

const ReservationModal: React.FC<ReservationModalProps> = ({
  open,
  onClose,
  onMobileBack,
  venueSlug,
  venueName,
  venueLogoUrl,
  venuePlans,
  address,
  city,
  tables,
  openingHours,
  timezone,
  country,
  minAdvanceBookingTime,
  minReservationTime,
  maxReservationTime,
  holdDurationSeconds,
  mobileSheet = false,
  bookingPolicyTiles = [],
  bookingPolicySection,
  editMode = false,
  originalReservation,
  reservationToken = "",
  onModifyComplete,
}) => {
  const [step, setStep] = useState<Step>("details")
  const [error, setError] = useState<string | null>(null)
  const { values, setFieldValue, resetForm } = useFormikContext<ReservationFormValues>()
  const { locale } = useLocale()

  const [createReservation, { loading: isCreating, data: createData }] = useMutation(PublicCreateReservationDocument)
  const [modifyReservation, { loading: isModifying, data: modifyData }] = useMutation(PublicModifyReservationDocument)

  const isSubmitting = isCreating || isModifying
  const hasBookingPolicy = bookingPolicyTiles.length > 0 || Boolean(bookingPolicySection?.content?.trim())

  const confirmationPayload = editMode ? modifyData?.publicModifyReservation : createData?.publicCreateReservation

  const confirmationReservation = confirmationPayload?.reservation

  const bookingPolicyStep = step === "booking-policy"
  const guestInformationStep = step === "guest-info"
  const confirmationStep = step === "confirmation"
  const compactDialogStep = bookingPolicyStep || guestInformationStep || confirmationStep

  const handleClose = async () => {
    const { date, peopleAmount, startTime, endTime } = values
    resetForm()

    if (step !== "confirmation" && !editMode) {
      await setFieldValue("date", date)
      await setFieldValue("peopleAmount", peopleAmount)
      await setFieldValue("startTime", startTime)
      await setFieldValue("endTime", endTime)
    }

    if (step === "confirmation" && editMode && confirmationReservation) {
      onModifyComplete?.(confirmationReservation.id)
    }

    setStep("details")
    setError(null)
    onClose()
  }

  // Mobile back-nav (decision #8): reopen the step-1 sheet with the shared Formik values intact.
  // Unlike handleClose, this does NOT reset the form, so date/party/time/table survive the hop.
  const handleMobileBack = () => {
    setError(null)
    setStep("details")
    onMobileBack?.()
  }

  const handleDetailsNext = () => {
    setError(null)
    if (hasBookingPolicy) {
      setStep("booking-policy")
    } else {
      setStep("guest-info")
    }
  }

  const handleBookingPolicyBack = () => {
    setError(null)
    setStep("details")
  }

  const handleBookingPolicyNext = () => {
    setError(null)
    setStep("guest-info")
  }

  const handleGuestInfoBack = () => {
    setError(null)
    if (hasBookingPolicy) {
      setStep("booking-policy")
    } else {
      setStep("details")
    }
  }

  const handleSubmit = async () => {
    setError(null)

    try {
      if (editMode && originalReservation) {
        const result = await modifyReservation({
          variables: {
            originalReservationId: originalReservation.id,
            venue: venueSlug,
            customer: {
              name: values.name,
              email: values.email,
              phone: values.phone,
              marketingConsent: values.marketingConsent,
            },
            startTime: values.startTime,
            endTime: values.endTime,
            locale,
            peopleAmount: values.peopleAmount,
            tableId: values.tableId,
            note: values.note || null,
            token: reservationToken,
          },
        })

        const reservation = result?.data?.publicModifyReservation?.reservation
        const errors = result?.data?.publicModifyReservation?.errors || []

        if (errors.length > 0) {
          const errorCode = errors[0].code
          if (errorCode === PublicModifyReservationErrorEnum.Overlaps) {
            setError(i18n.t("app.public.new_reservation.errors.reservation_overlaps"))
            return
          }
          if (errorCode === PublicModifyReservationErrorEnum.VenueClosed) {
            setError(i18n.t("app.public.new_reservation.errors.venue_closed"))
            return
          }
          if (errorCode === PublicModifyReservationErrorEnum.ModificationDeadlinePassed) {
            setError(i18n.t("app.public.new_reservation.errors.modification_deadline_passed"))
            return
          }
          if (errorCode === PublicModifyReservationErrorEnum.NotModifiable) {
            setError(i18n.t("app.public.new_reservation.errors.not_modifiable"))
            return
          }
          setError(i18n.t("app.public.new_reservation.errors.generic"))
          return
        }

        if (!reservation) {
          setError(i18n.t("app.public.new_reservation.errors.generic"))
          return
        }

        setStep("confirmation")
      } else {
        const result = await createReservation({
          variables: {
            venue: venueSlug,
            customer: {
              name: values.name,
              email: values.email,
              phone: values.phone,
              marketingConsent: values.marketingConsent,
            },
            startTime: values.startTime,
            endTime: values.endTime,
            locale,
            peopleAmount: values.peopleAmount,
            tableId: values.tableId,
            note: values.note || null,
          },
        })

        const reservation = result?.data?.publicCreateReservation?.reservation
        const errors = result?.data?.publicCreateReservation?.errors || []

        if (errors.length > 0) {
          const errorCode = errors[0].code
          if (errorCode === PublicCreateReservationErrorEnum.Overlaps) {
            setError(i18n.t("app.public.new_reservation.errors.reservation_overlaps"))
            return
          }
          if (errorCode === PublicCreateReservationErrorEnum.VenueClosed) {
            setError(i18n.t("app.public.new_reservation.errors.venue_closed"))
            return
          }
          setError(i18n.t("app.public.new_reservation.errors.generic"))
          return
        }

        if (!reservation) {
          setError(i18n.t("app.public.new_reservation.errors.generic"))
          return
        }

        setStep("confirmation")
      }
    } catch (err) {
      console.error("Error submitting reservation:", err)
      setError(i18n.t("app.public.new_reservation.errors.generic"))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      data-testid="public-reservation-modal-container"
      sx={
        mobileSheet
          ? {
              "& .MuiDialog-container": {
                alignItems: "flex-end",
              },
            }
          : undefined
      }
      PaperProps={{
        "data-testid": mobileSheet
          ? "public-reservation-modal-mobile-sheet"
          : "public-reservation-modal-desktop-dialog",
        sx: {
          borderRadius: mobileSheet ? "10px 10px 0 0" : { xs: 0, sm: "10px" },
          maxWidth: mobileSheet ? "100%" : { xs: "100%", sm: compactDialogStep ? "500px" : "970px" },
          minHeight: mobileSheet ? "auto" : compactDialogStep ? undefined : { xs: "100vh", sm: "500px" },
          maxHeight: mobileSheet
            ? "calc(100vh - 60px)"
            : compactDialogStep
              ? { xs: "100vh", sm: "calc(100vh - 64px)" }
              : { xs: "100vh", sm: "min(700px, 85vh)" },
          height: mobileSheet ? undefined : compactDialogStep ? "auto" : { xs: "100vh", sm: "min(700px, 85vh)" },
          width: mobileSheet ? "100%" : undefined,
          margin: mobileSheet ? 0 : undefined,
          overflowX: "hidden",
          overflowY: mobileSheet || compactDialogStep ? "auto" : "hidden",
        },
      }}
    >
      {error ? (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          className="m-4"
          data-testid="public-reservation-modal-error"
        >
          {error}
        </Alert>
      ) : null}

      {step === "details" && (
        <ReservationDetailsStep
          tables={tables}
          openingHours={openingHours}
          timezone={timezone}
          country={country}
          venueSlug={venueSlug}
          venueName={venueName}
          venueLogoUrl={venueLogoUrl}
          venuePlans={venuePlans}
          address={address}
          city={city}
          minAdvanceBookingTime={minAdvanceBookingTime}
          minReservationTime={minReservationTime}
          maxReservationTime={maxReservationTime}
          mobileSheet={mobileSheet}
          onBack={mobileSheet && onMobileBack ? handleMobileBack : handleClose}
          onClose={handleClose}
          onNext={handleDetailsNext}
          onError={setError}
          editMode={editMode}
          originalReservation={originalReservation}
        />
      )}

      {step === "booking-policy" && (
        <BookingPolicyStep
          holdDurationSeconds={holdDurationSeconds}
          bookingPolicyTiles={bookingPolicyTiles}
          bookingPolicySection={bookingPolicySection}
          mobileSheet={mobileSheet}
          onBack={handleBookingPolicyBack}
          onNext={handleBookingPolicyNext}
          onClose={handleClose}
        />
      )}

      {step === "guest-info" && (
        <GuestInformationStep
          holdDurationSeconds={holdDurationSeconds}
          country={country}
          timezone={timezone}
          tables={tables}
          mobileSheet={mobileSheet}
          onBack={handleGuestInfoBack}
          onClose={handleClose}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {step === "confirmation" && confirmationReservation ? (
        <ReservationConfirmationStep
          reservation={confirmationReservation}
          confirmUntil={confirmationPayload?.confirmUntil}
          resendAvailableAt={confirmationPayload?.resendAvailableAt}
          resendsRemaining={confirmationPayload?.resendsRemaining || 0}
          onClose={handleClose}
          reservationToken={reservationToken}
          customerEmail={values.email}
        />
      ) : null}
    </Dialog>
  )
}

export default ReservationModal
